import { z } from "zod";

/**
 * Table Column Schema
 * Defines the structure and behavior of individual table columns
 */
const TableColumnSchema = z.object({
  key: z.string().min(1, "Column key is required - must match property names in data objects (e.g., 'name', 'sales', 'region')"),
  header: z.string().min(1, "Column header is required - user-friendly display name for the column (e.g., 'Sales Representative', 'Total Revenue')"),
  sortable: z.boolean().optional().describe("Set to true to enable sorting for this column - useful for numeric data, dates, and categorical data"),
  filterable: z.boolean().optional().describe("Set to true to enable filtering for this column - useful for text fields and categories that users might want to search"),
  width: z.number().positive().optional().describe("Optional column width in pixels - use for fixed-width columns or to prevent text overflow"),
  align: z.enum(['left', 'center', 'right']).optional().describe("Text alignment: 'left' for text/names, 'right' for numbers/currency, 'center' for status/categories"),
});

/**
 * Table Configuration Schema
 * Controls table behavior and appearance
 */
const TableConfigSchema = z.object({
  enableSorting: z.boolean().optional().describe("Global sorting toggle - set to true to allow users to sort by clickable column headers"),
  enableFiltering: z.boolean().optional().describe("Global filtering toggle - set to true to show search box and column-specific filters"),
  enablePagination: z.boolean().optional().describe("Pagination toggle - set to true for large datasets (>20 rows) to improve performance and UX"),
  pageSize: z.number().positive().max(100).optional().describe("Number of rows per page (1-100) - recommended: 10 for detailed data, 20-50 for summary data"),
  striped: z.boolean().optional().describe("Alternating row colors - set to true for better readability with many rows"),
}).optional();

/**
 * Table Block Schema - Best for structured data that users need to explore, compare, or analyze
 * Use for: Sales reports, user lists, product catalogs, financial data, inventory, performance metrics
 * 
 * Guidelines:
 * - Use tables when data has multiple attributes per item
 * - Enable sorting for columns users might want to rank (sales, dates, names)
 * - Enable filtering when dataset is large or users need to find specific items
 * - Use pagination for datasets with >20 rows
 * - Align numbers right, text left, categories/status center
 */
const TableBlockSchema = z.object({
  type: z.literal("table").describe("Block type identifier - must be 'table' for table blocks"),
  title: z.string().optional().describe("Optional descriptive title for the table (e.g., 'Q4 Sales Performance by Representative', 'Product Inventory Status')"),
  columns: z.array(TableColumnSchema).min(1, "Table must have at least one column - define columns that represent meaningful data attributes"),
  data: z.array(z.record(z.string(), z.any())).min(1, "Table must have at least one row of data - each object represents a row with properties matching column keys"),
  config: TableConfigSchema.describe("Optional configuration for table behavior including sorting, filtering, and pagination"),
});

/**
 * Table Validation Function - Validates table data against the schema
 * 
 * @param table - The table object to validate
 * @returns Validated table object with proper typing
 * @throws Error with descriptive message if validation fails
 * 
 * Use this function before rendering tables to ensure data integrity.
 * Validates that column keys match data properties and all required fields are present.
 */
export const validateTable = (table: unknown) => {
  try {
    const validatedTable = TableBlockSchema.parse(table);
    
    // Additional validation: ensure all column keys exist in data
    const dataKeys = new Set();
    validatedTable.data.forEach(row => {
      Object.keys(row).forEach(key => dataKeys.add(key));
    });
    
    const missingKeys = validatedTable.columns
      .map(col => col.key)
      .filter(key => !dataKeys.has(key));
    
    if (missingKeys.length > 0) {
      throw new Error(`Column keys not found in data: ${missingKeys.join(', ')}. Ensure all column keys match properties in your data objects.`);
    }
    
    return validatedTable;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map(issue => {
        const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${issue.message}`;
      });
      throw new Error(`Table validation failed: ${issues.join(', ')}. Please check your table structure and ensure all required fields are provided with correct types.`);
    }
    throw new Error(`Table validation failed: ${error instanceof Error ? error.message : 'Unknown error occurred during validation'}`);
  }
};

// Export the schema for use in other validators or type inference
export const TableSchema = TableBlockSchema;
